import React, { useState, useEffect } from 'react';
import api from '../api';
import { useTheme } from '../context/ThemeContext';

const AuditLogs = () => {
  const { theme } = useTheme();
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLog, setSelectedLog] = useState(null);
  
  // Filters
  const [filterUserEmail, setFilterUserEmail] = useState('');
  const [filterEventType, setFilterEventType] = useState('');
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');
  const [filterReason, setFilterReason] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Metadata for filters
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableEventTypes, setAvailableEventTypes] = useState([]);
  const [availableReasons, setAvailableReasons] = useState([]);
  
  // Pagination
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(null);
  const [hasMore, setHasMore] = useState(false);

  useEffect(() => {
    fetchMetadata();
    fetchAuditLogs();
  }, []);

  useEffect(() => {
  const key = getSelectedEventKey();
  if (key !== 'CREDITS_ADDED') {
    setFilterReason('');
  }
}, [filterEventType, availableEventTypes]);

  const getSelectedEventKey = () => {
    if (!filterEventType || availableEventTypes.length === 0) return null;
    const found = availableEventTypes.find(et => et.key === filterEventType || et.value === filterEventType || et.description === filterEventType);
    return found ? (found.key || found.value) : null;
  };

  const fetchMetadata = async () => {
    try {
      const [usersResponse, eventTypesResponse] = await Promise.all([
        api.get('/api/audit/meta/users'),
        api.get('/api/audit/meta/event-types')
      ]);
      setAvailableUsers(usersResponse.data.data || []);
      setAvailableEventTypes(eventTypesResponse.data.data || []);
      setAvailableReasons([
        "Bonus credits",
        "Purchased"
      ]);
    } catch (err) {
      console.error('Error fetching metadata:', err);
    }
  };

  const fetchAuditLogs = async (resetPagination = true) => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filterUserEmail) params.append('userEmail', filterUserEmail);
      if (filterEventType) params.append('eventType', filterEventType);
      if (filterReason) params.append('reason', filterReason);
      if (filterStartDate) params.append('startDate', new Date(filterStartDate).toISOString());
      if (filterEndDate) params.append('endDate', new Date(filterEndDate).toISOString());
      if (!resetPagination && lastEvaluatedKey) params.append('lastEvaluatedKey', lastEvaluatedKey);

      const response = await api.get(`/api/audit?${params.toString()}`);
      const data = response.data.data;

      if (resetPagination) {
        setAuditLogs(data.items || []);
      } else {
        setAuditLogs(prev => [...prev, ...(data.items || [])]);
      }

      setLastEvaluatedKey(data.lastEvaluatedKey);
      setHasMore(!!data.lastEvaluatedKey);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError(err.response?.data?.error || 'Failed to fetch audit logs');
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    fetchAuditLogs(true);
  };

  const handleClearFilters = async () => {
    setFilterUserEmail('');
    setFilterEventType('');
    setFilterReason('');
    setFilterStartDate('');
    setFilterEndDate('');
    setLastEvaluatedKey(null);
    setHasMore(false);
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/api/audit');
      const data = response.data.data;
      setAuditLogs(data.items || []);
      setLastEvaluatedKey(data.lastEvaluatedKey);
      setHasMore(!!data.lastEvaluatedKey);
    } catch (err) {
      console.error('Error fetching audit logs:', err);
      setError(err.response?.data?.error || 'Failed to fetch audit logs');} finally {
        setLoading(false);
      }
    };

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filterUserEmail) params.append('userEmail', filterUserEmail);
      if (filterEventType) params.append('eventType', filterEventType);
      if (filterStartDate) params.append('startDate', new Date(filterStartDate).toISOString());
      if (filterEndDate) params.append('endDate', new Date(filterEndDate).toISOString());

      const response = await api.get(`/api/audit/export/csv?${params.toString()}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `audit-logs-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Error exporting audit logs:', err);
      alert('Failed to export audit logs');
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleString();
  };

  const displayedLogs = auditLogs.filter(log => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return [log.description, log.eventType, log.actorName, log.actorEmail, log.reason, JSON.stringify(log.eventData || {})].some(field => (field || '').toLowerCase().includes(q));
  });

  const getEventTypeColor = (eventType) => {
    if (eventType.includes('CREATED')) return '#28a745';
    if (eventType.includes('UPDATED')) return '#ffc107';
    if (eventType.includes('DELETED')) return '#dc3545';
    if (eventType.includes('GENERATED')) return '#17a2b8';
    if (eventType.includes('VIEWED') || eventType.includes('LISTED')) return '#6c757d';
    return '#007bff';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'success': return '#28a745';
      case 'failed': return '#dc3545';
      case 'partial': return '#ffc107';
      default: return '#6c757d';
    }
  };

  return (
    <div style={{ padding: '30px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '30px'
      }}>
        <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 'bold', color: '#2c3e50' }}>
          📜 Audit Logs
        </h1>
        <input
        type="text"
        placeholder="Search"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        style={{
          padding: '10px 14px',
          borderRadius: '6px',
          border: '1px solid #ccc',
          fontSize: '14px',
          width: '250px',
          marginRight: '15px'
        }}
        />
        <button
          onClick={handleExport}
          style={{
            padding: '10px 20px',
            backgroundColor: '#28a745',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '500',
          }}
        >
          📥 Export to CSV
        </button>
      </div>

      {/* Filters */}
      <div style={{
        backgroundColor: 'white',
        padding: '25px',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        marginBottom: '25px'
      }}>
        <h3 style={{ marginTop: 0, marginBottom: '20px', fontSize: '18px' }}>Filters</h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '15px',
          marginBottom: '15px'
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
              User Email
            </label>
            <select
              value={filterUserEmail}
              onChange={(e) => setFilterUserEmail(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="">All Users</option>
              {availableUsers.map(user => (
                <option key={user.email} value={user.email}>
                  {user.name} ({user.email})
                </option>
              ))}
            </select>
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
              Event Type
            </label>
            <select
              value={filterEventType}
              onChange={(e) => setFilterEventType(e.target.value)}
              style={{
                width: '100%',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            >
              <option value="">All Events</option>
              {availableEventTypes.map(eventType => (
                <option key={eventType.value} value={eventType.value}>
                  {eventType.description}
                </option>
              ))}
            </select>
          </div>
          {getSelectedEventKey() === "CREDITS_ADDED" && (
            <div>
              <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
                Reason
              </label>
              <select
                value={filterReason}
                onChange={(e) => setFilterReason(e.target.value)}
                style={{
                  width: '200px',
                  padding: '8px 12px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              >
                <option value="">All Reasons</option>
                {availableReasons.map(reason => (
                  <option key={reason} value={reason}>
                    {reason}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
              Start Date
            </label>
            <input
              type="datetime-local"
              value={filterStartDate}
              onChange={(e) => setFilterStartDate(e.target.value)}
              style={{
                width: '200px',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>

          <div>
            <label style={{ display: 'block', marginBottom: '5px', fontSize: '14px', fontWeight: '500' }}>
              End Date
            </label>
            <input
              type="datetime-local"
              value={filterEndDate}
              onChange={(e) => setFilterEndDate(e.target.value)}
              style={{
                width: '200px',
                padding: '8px 12px',
                border: '1px solid #ddd',
                borderRadius: '6px',
                fontSize: '14px'
              }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            onClick={handleFilter}
            style={{
              padding: '10px 20px',
              backgroundColor: '#007bff',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            Apply Filters
          </button>
          <button
            onClick={handleClearFilters}
            style={{
              padding: '10px 20px',
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
            }}
          >
            Clear Filters
          </button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div style={{
          backgroundColor: '#f8d7da',
          color: '#721c24',
          padding: '15px',
          borderRadius: '6px',
          marginBottom: '20px'
        }}>
          {error}
        </div>
      )}

      {/* Audit Logs Table */}
      {loading && auditLogs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '4px solid #e3e3e3',
            borderTop: '4px solid #007bff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 20px'
          }} />
          <p>Loading audit logs...</p>
        </div>
      ) : auditLogs.length === 0 ? (
        <div style={{
          backgroundColor: 'white',
          padding: '50px',
          borderRadius: '12px',
          textAlign: 'center',
          color: '#6c757d'
        }}>
          <p style={{ fontSize: '18px', margin: 0 }}>No audit logs found</p>
        </div>
      ) : (
        <>
          {hasMore && (
            <div style={{
              textAlign: 'right',
              marginBottom: '10px',
              paddingRight: '5px'
            }}>
              <button
                onClick={() => fetchAuditLogs(false)}
                disabled={loading}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#007bff',
                  textDecoration: 'underline',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '13px',
                  padding: 0
                }}
              >
                {loading ? "Loading…" : "Load next records →"}
              </button>
            </div>
          )}
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            overflow: 'hidden'
          }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>
                    Event Type
                  </th>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>
                    User
                  </th>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>
                    Timestamp
                  </th>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>
                    Reason
                  </th>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>
                    Description
                  </th>
                  <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>
                    Status
                  </th>
                  <th style={{ padding: '15px', textAlign: 'center', fontWeight: '600', fontSize: '14px' }}>
                    Details
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayedLogs.map((log, index) => (
                  <tr
                    key={log.auditLogId}
                    style={{
                      borderBottom: '1px solid #dee2e6',
                      backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa'
                    }}
                  >
                    <td style={{ padding: '15px' }}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: `${getEventTypeColor(log.eventType)}20`,
                        color: getEventTypeColor(log.eventType)
                      }}>
                        {log.eventType}
                      </span>
                    </td>
                    <td style={{ padding: '15px', fontSize: '13px' }}>
                      <div style={{ fontWeight: '500', color: '#2c3e50' }}>{log.actorName}</div>
                      <div style={{ fontSize: '12px', color: '#6c757d' }}>{log.actorEmail}</div>
                    </td>
                    <td style={{ padding: '15px', fontSize: '13px', color: '#495057' }}>
                      {formatTimestamp(log.timestamp)}
                    </td>
                    <td style={{ padding: '15px', fontSize: '13px', color: '#495057' }}>
                      {log.reason || '-'}
                    </td>
                    <td style={{ padding: '15px', fontSize: '13px', color: '#495057' }}>
                      {log.description}
                    </td>
                    <td style={{ padding: '15px' }}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '500',
                        backgroundColor: `${getStatusColor(log.status)}20`,
                        color: getStatusColor(log.status)
                      }}>
                        {log.status}
                      </span>
                    </td>
                    <td style={{ padding: '15px', textAlign: 'center' }}>
                      <button
                        onClick={() => setSelectedLog(log)}
                        style={{
                          padding: '6px 12px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Load More Button */}
          {hasMore && (
            <div style={{ textAlign: 'center', marginTop: '20px' }}>
              <button
                onClick={() => fetchAuditLogs(false)}
                disabled={loading}
                style={{
                  padding: '10px 30px',
                  backgroundColor: loading ? '#6c757d' : '#007bff',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                }}
              >
                {loading ? 'Loading...' : 'Load More'}
              </button>
            </div>
          )}
        </>
      )}

      {/* Detail Modal */}
      {selectedLog && (
        <div
          onClick={() => setSelectedLog(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              backgroundColor: theme.cardBackground,
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '700px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              border: `1px solid ${theme.border}`
            }}
          >
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px'
            }}>
              <h2 style={{ margin: 0, fontSize: '24px', color: theme.text }}>Audit Log Details</h2>
              <button
                onClick={() => setSelectedLog(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: theme.textMuted
                }}
              >
                ×
              </button>
            </div>

            <div style={{ display: 'grid', gap: '15px' }}>
              {Object.entries(selectedLog).map(([key, value]) => {
                if (key === 'eventData' && typeof value === 'object') {
                  return (
                    <div key={key}>
                      <div style={{ fontWeight: '600', color: theme.text, marginBottom: '5px' }}>
                        Event Data:
                      </div>
                      <pre style={{
                        backgroundColor: theme.tableHeader,
                        padding: '15px',
                        borderRadius: '6px',
                        fontSize: '12px',
                        overflow: 'auto',
                        margin: 0,
                        color: theme.text
                      }}>
                        {JSON.stringify(value, null, 2)}
                      </pre>
                    </div>
                  );
                }

                return (
                  <div key={key}>
                    <div style={{ fontWeight: '600', color: theme.text, marginBottom: '5px' }}>
                      {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}:
                    </div>
                    <div style={{ color: theme.textSecondary, fontSize: '14px' }}>
                      {value !== null && value !== undefined ? String(value) : 'N/A'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default AuditLogs;
